import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { Result } from '@cplug/frontend-core/utils'
import { ErrorCodes, type BaseError } from '@cplug/frontend-core/models'
import { useWhitelabelStore } from '@cplug/frontend-shared-domains/domain/whitelabel'
import { useMyPlanStore, type MyPlan } from '@cplug/frontend-subscription-hub/domain/plans/my-plan'
import { CPResearchCustomerGateway } from '../gateways/CPResearchCustomerGateway'
import type { ActiveResearchCustomer } from '../models/ActiveResearchCustomer'

function hasActivePaidPlan (plan: MyPlan | undefined): boolean {
  return plan?.plan != null
}

const useResearchCustomerStore = defineStore('shared/research-customer', () => {
  const gateway = CPResearchCustomerGateway.instance()
  const myPlanStore = useMyPlanStore()
  const whitelabelStore = useWhitelabelStore()
  const activeResearch = ref<ActiveResearchCustomer | undefined>()
  const activeResearchLoaded = ref(false)
  const errors = ref<BaseError[]>([])

  const whitelabelSkipResearchCustomer = computed(
    () => whitelabelStore.isWhitelabel || whitelabelStore.isPartner
  )

  async function ensureWhitelabelSettings (): Promise<void> {
    if (whitelabelStore.settings == null) {
      await whitelabelStore.getSettings()
    }
  }

  async function ensureActivePaidPlan (): Promise<boolean> {
    if (myPlanStore.model == null) {
      await myPlanStore.getMyPlan()
    }
    return hasActivePaidPlan(myPlanStore.model)
  }

  async function shouldSkipResearchCustomer (): Promise<boolean> {
    await ensureWhitelabelSettings()

    if (whitelabelSkipResearchCustomer.value) {
      return true
    }
    return !(await ensureActivePaidPlan())
  }

  function finishGetActive (ok: boolean): boolean {
    activeResearchLoaded.value = true
    return ok
  }

  async function getActive (): Promise<boolean> {
    if (activeResearch.value?.completed) {
      return finishGetActive(false)
    }

    if (await shouldSkipResearchCustomer()) {
      activeResearch.value = undefined
      errors.value = []
      return finishGetActive(false)
    }

    const wrapped = await Result.ofPromise(gateway.getActive())

    if (wrapped.isError()) {
      errors.value = [wrapped.getError() ?? { code: ErrorCodes.Unknown }]
      activeResearch.value = undefined
      return finishGetActive(false)
    }

    const result = wrapped.getValue()

    if (result == null || result.isError()) {
      errors.value = [result?.getError() ?? { code: ErrorCodes.Unknown }]
      activeResearch.value = undefined
      return finishGetActive(false)
    }

    activeResearch.value = result.getValue()
    errors.value = []
    return finishGetActive(true)
  }

  async function submitActive (): Promise<boolean> {
    if (await shouldSkipResearchCustomer()) {
      errors.value = []
      return false
    }

    const researchId = activeResearch.value?.researchId

    if (researchId == null) {
      errors.value = [{ code: ErrorCodes.Unknown }]
      return false
    }

    const wrapped = await Result.ofPromise(gateway.updateActive({
      researchId,
      researchCustomerId: activeResearch.value?.researchCustomerId
    }))

    if (wrapped.isError()) {
      errors.value = [wrapped.getError() ?? { code: ErrorCodes.Unknown }]
      return false
    }

    const result = wrapped.getValue()

    if (result == null || result.isError()) {
      errors.value = [result?.getError() ?? { code: ErrorCodes.Unknown }]
      return false
    }

    activeResearch.value = result.getValue()
    errors.value = []
    return true
  }

  function $reset (): void {
    activeResearch.value = undefined
    activeResearchLoaded.value = false
    errors.value = []
  }

  return { activeResearch, activeResearchLoaded, errors, getActive, submitActive, $reset }
})

export default useResearchCustomerStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useResearchCustomerStore, (import.meta as any).hot))
}
